import React, { useState, useEffect, useContext, useCallback } from "react";

import DataTable from "react-data-table-component";

import Loading from "../../../../components/Loading";
import { withRouter, useParams } from "react-router-dom";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiWarningFill } from "react-icons/pi";
import HeaderNavigation from "components/PeopleCraft/HeaderNavigation";
import { AuthContext } from "context/AuthContext";
import { MdEdit } from "react-icons/md";
import { LuTrash2 } from "react-icons/lu";
import { ImportFiles } from "../ImportFiles";
import RemoveFile from "assets/images/remove-file.svg";
import {
  attachmentUpload,
  getAttachmentExtension,
  getAttachments,
  attachmentRename,
  attachmentRemove,
  getToken,
  createAttachments,
  getAttachment,
} from "services/NexiDigitalAPI";
import "./style.modules.scss";
import { toast, ToastContainer } from "react-toastify";
import PreloaderBlur from "containers/PeopleCraft/PreloaderBlur";

const MAX_FILE_SIZE_MB = 25;
const MAX_FILES_PER_PAGE = 10;
const paginationComponentOptions = {
  rowsPerPageText: "Arquivos por página",
  rangeSeparatorText: "de",
};

const DeleteAttachmentModal = ({
  show,
  title,
  description,
  closeText,
  deleteText,
  handleClose,
  handleDelete,
}) => {
  return (
    <Modal show={show} onHide={handleClose} centered className="delete-attachment-alert">
      <Modal.Body className="text-center">
        <img className="title-modal-icon" src={RemoveFile} alt={"remove-file"} />
        <h5 className="title-modal-text">{title}</h5>
        <p className="description-modal-text">{description}</p>
      </Modal.Body>
      <Modal.Footer className="modal-footer">
        <div className="footer-buttons">
          <Button variant="light" className="cancel-button" onClick={handleClose}>
            {closeText}
          </Button>
          <Button variant="danger" className="delete-button" onClick={handleDelete}>
            {deleteText}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const ClassAttachment = (props) => {
  const { pageTitle, teamName, idTeam } = useParams();
  const [filesList, setFilesList] = useState([]);
  const [totalRows, setTotalRows] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [removeAlertProps, setRemoveAlertProps] = useState({
    show: false,
  });
  const [contentLoading, setContentLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const [editingFileIndex, setEditingFileIndex] = useState(null);
  const [newFileName, setNewFileName] = useState("");
  const [acceptedFilesExtension, setAcceptedFilesExtension] = useState([]);

  const getExtensions = async () => {
    const {
      data: { extensions },
    } = await getAttachmentExtension();

    // console.log(extensions);
    setAcceptedFilesExtension(extensions);
  };

  const uploadAttachment = async (input) => {
    setContentLoading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append("fileData", input.file);
    formData.append("fileName", input.title);
    formData.append("token", token);

    // console.log("input", input);

    const {
      data: { url },
    } = await attachmentUpload(formData);

    await createAttachments(idTeam, {
      title: input.title,
      format: input.format,
      url,
    });

    fetchData(0, MAX_FILES_PER_PAGE);
    setContentLoading(false);
  };

  const {
    loginData: { menuData },
  } = useContext(AuthContext);

  const fetchData = useCallback(
    async (offset, limit = MAX_FILES_PER_PAGE) => {
      try {
        setLoadingTable(true);
        const {
          data: { result, totalRecords },
        } = await getAttachments(idTeam, { offset, limit, notInTeam: false });

        setFilesList(result);
        setTotalRows(totalRecords);
      } catch (e) {
        toast.error(`Error ao tentar buscar arquivo`);

        setFilesList([]);
      } finally {
        setLoadingTable(false);
      }
    },
    [idTeam],
  );

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * perPage;
    setWarningMessage(null);
    fetchData(newOffset, perPage);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setWarningMessage(null);
    setPerPage(newPerPage);
  };

  const handleSaveFileName = async (idAttachment) => {
    if (!newFileName) {
      setEditingFileIndex(null);
      return;
    }
    try {
      await attachmentRename(idAttachment, {
        title: newFileName,
      });

      setFilesList((prevFiles) =>
        prevFiles.map((file) =>
          file.idAttachment === idAttachment ? { ...file, title: newFileName } : file,
        ),
      );
      setEditingFileIndex(null);
      setNewFileName("");
    } catch (error) {
      toast.error(`Error ao tentar anexar arquivo`);
    }
  };

  const removeAttachment = async (idAttachment) => {
    try {
      setContentLoading(true);
      await attachmentRemove(idAttachment, idTeam);

      setFilesList((prevFiles) => prevFiles.filter((file) => file.idAttachment !== idAttachment));

      setTotalRows((prev) => prev - 1);
      setRemoveAlertProps({ show: false });
    } catch (error) {
      toast.error(`Error ao tentar remover arquivo`);
    } finally {
      setContentLoading(false);
    }
  };

  const handleRemoveFile = async (idAttachment) => {
    try {
      const alertProps = {
        show: true,
        title: "Excluir anexo de turma?",
        description: "Essa ação é irreversível.",
        closeText: "Cancelar",
        deleteText: "Excluir",
        handleClose: () => setRemoveAlertProps({ show: false }),
        handleDelete: () => {
          removeAttachment(idAttachment);
        },
      };

      const {
        data: { usedInOtherTeams },
      } = await getAttachment(idAttachment, idTeam);

      if (usedInOtherTeams) {
        alertProps.title = "Remover anexo de turma?";
        alertProps.description =
          "Este arquivo já está em uso em outros locais, por isso será removido apenas desta turma.";
        alertProps.deleteText = "Remover";
      }

      setRemoveAlertProps(alertProps);
    } catch (error) {
      toast.error(`Error ao tentar remover arquivo`);
    }
  };

  const saveFileOnPressEnter = (e, currentFileIdAttachment) => {
    if (e.key === "Enter") {
      handleSaveFileName(currentFileIdAttachment);
    }
  };

  const columns = [
    {
      name: "Nome do arquivo",
      selector: "title",
      sortable: true,
      cell: (currentFile) => {
        return editingFileIndex === currentFile.idAttachment ? (
          <input
            type="text"
            defaultValue={currentFile.title}
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => saveFileOnPressEnter(e, currentFile.idAttachment)}
            onBlur={() => {
              setEditingFileIndex(null);
              setNewFileName("");
            }}
            autoFocus
          />
        ) : (
          currentFile.title
        );
      },
    },
    {
      name: "Formato",
      selector: "format",
      sortable: true,
    },
    {
      name: "Criador por",
      selector: "creatorName",
      sortable: true,
    },
    {
      name: "Ações",
      width: "200px",
      selector: "action",
      cell: (currentFile) => [
        <div key={currentFile.idAttachment} className="listBtn">
          <OverlayTrigger
            placement={"top"}
            overlay={<Tooltip id={`tooltip-top`}> Renomear </Tooltip>}
          >
            <button
              type="button"
              onClick={() => {
                // console.log("currentFile", currentFile);
                setEditingFileIndex(currentFile.idAttachment);
                setNewFileName(currentFile.fileName);
                setWarningMessage(null);
              }}
            >
              <MdEdit size={18} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement={"top"}
            overlay={<Tooltip id={`tooltip-top`}> Remover </Tooltip>}
          >
            <button type="button" onClick={() => {
              handleRemoveFile(currentFile.idAttachment);
              setWarningMessage(null);
              }}>
              <LuTrash2 size={18} />
            </button>
          </OverlayTrigger>
        </div>,
      ],
    },
  ];

  const customTableStyles = {
    table: {
      style: {
        maxHeight: "70vh",
        overflow: "auto",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#6b48ff",
        color: "white",
        paddingLeft: "30px",
      },
    },
    cells: {
      style: {
        paddingLeft: "30px",
      },
    },
  };

  const CreateNewButton = () => {
    const showOpenFileDlg = () => {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = acceptedFilesExtension;
      fileInput.onchange = handleFileChange;
      setWarningMessage(null);

      fileInput.click();
    };

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          setWarningMessage(
            `O arquivo que você tentou fazer o upload excede o tamanho máximo. O limite de tamanho é de ${MAX_FILE_SIZE_MB} MB por arquivo.`,
          );
          return;
        }

        const fileType = file.name.split(".")[1];

        // console.log(file, "-", fileType);

        if (!acceptedFilesExtension.includes(fileType)) {
          setWarningMessage(
            `Arquivo não permitido. Extensões permitidas: ${acceptedFilesExtension}`,
          );
          return;
        }

        const { name: currentUserName } = menuData;

        uploadAttachment({
          title: file.name.replace(`.${fileType}`, ""),
          format: fileType.toUpperCase(),
          createdBy: currentUserName,
          file,
        });
      }
    };

    return (
      <button onClick={showOpenFileDlg} className="class-attachment-create-new btn-custom">
        Cadastrar Novo
      </button>
    );
  };

  const ImportNewButton = () => {
    return (
      <button onClick={() => {
            setWarningMessage(null);
            setIsImportModalOpen(true)
          }
        } className="class-attachment-import-new">
        Importar
      </button>
    );
  };

  const EmptyAttaches = () => {
    return (
      <div className="class-attachment-empty-container">
        <div className="class-attachment-empty-header">
          <span className="class-attachment-empty-container-title">
            Ainda não há anexo cadastrado para esta turma.
          </span>
          <span className="class-attachment-empty-container-description">
            Comece cadastrando ou importando um arquivo
          </span>
          <div className="class-attachment-empty-container-buttons">
            <CreateNewButton />
            <ImportNewButton />
          </div>
        </div>
      </div>
    );
  };

  const AttachmentHeader = () => {
    return <HeaderNavigation title={`Termos da plataforma  - ${pageTitle || ""}`} />;
  };

  const WarningMessage = () => {
    return (
      <div variant="light" className="d-flex align-items-center class-attachment-alert">
        <div className="class-attachment-alert-content-wall"></div>
        <div className="class-attachment-alert-content">
          <PiWarningFill className="class-attachment-alert-icon" />
          <span className="class-attachment-alert-message">{warningMessage}</span>
        </div>
      </div>
    );
  };

  const onCloseImport = (update = false) => {
    if (update) {
      fetchData(0, perPage);
    }
    setIsImportModalOpen();
  };

  useEffect(() => {
    getExtensions();
  }, []);

  useEffect(() => {
    fetchData(0, perPage);
  }, [perPage]);



  return (
    <div className="class-attachment">
      <AttachmentHeader />
      {contentLoading && <PreloaderBlur />}
      <div className="class-attachment-container">
        <div className="class-attachment-title">
          <h4>
            {pageTitle} - {teamName}
          </h4>
          <p className="file-rule">
            Gerencie abaixo os anexos da turma (formatos aceitos: {acceptedFilesExtension}).
            <strong> Tamanho máximo por arquivo {MAX_FILE_SIZE_MB} MB</strong>.
          </p>
        </div>
        <div style={{ width: "100%" }}>
          {warningMessage && <WarningMessage />}
          {filesList.length > 0 && (
            <div className="class-attachment-table-container-buttons">
              <CreateNewButton />
              <ImportNewButton />
            </div>
          )}

          <DataTable
            columns={columns}
            customStyles={customTableStyles}
            data={filesList}
            progressComponent={
              <Loading containerStyle={{ height: 400 }}>Buscando termos...</Loading>
            }
            noDataComponent={
              <div className="class-attachment-content">
                <div className="content-page-card">
                  <EmptyAttaches />
                </div>
              </div>
            }
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationTotalRows={totalRows}
            progressPending={loadingTable}
            paginationPerPage={MAX_FILES_PER_PAGE}
            paginationComponentOptions={paginationComponentOptions}
            paginationServer
            fixedHeader
            pagination
            highlightOnHover
          />
        </div>
      </div>
      <div className="remove-alert">
        <DeleteAttachmentModal {...removeAlertProps} />
      </div>
      <Modal
        show={isImportModalOpen}
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="xl"
      >
        {isImportModalOpen && <ImportFiles id={idTeam} onClose={onCloseImport} />}
      </Modal>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default withRouter(ClassAttachment);
